import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  HStack,
  IconButton,
  NumberInput,
  NumberInputField,
  Text,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { useRef, useState } from 'react';
import { MdCancel, MdDelete, MdEdit, MdSave } from 'react-icons/md';
import { translate } from '../../../i18n';
import { OrganizationSportGameDurationDto, UpdateOrganizationSportGameDurationDto } from '../../../models/organization/dto/organization-sport-game-duration.dto';
import {
  useDeleteOrganizationGameDuration,
  useUpdateOrganizationGameDuration,
} from '../../organization/hooks/useOrganizationGameDurations';
import { useSelectedOrganization } from '../../organization/hooks/useSelectedOrganization';
import { buildDurationWriteWarning } from '../utils/duration-write-warnings';

interface Props {
  gameDuration: OrganizationSportGameDurationDto;
}

export default function GameDurationEditor({ gameDuration }: Props) {
  const { selectedOrganization } = useSelectedOrganization();
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState<string>(String(gameDuration.durationMinutes));
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const cardBg = useColorModeValue('white', 'gray.700');
  const cardBorderColor = useColorModeValue('gray.200', 'gray.600');

  const { mutateAsync: updateGameDuration, isLoading: isUpdating } = useUpdateOrganizationGameDuration(
    selectedOrganization?.id || '',
    gameDuration.id,
    (result) => {
      // Moving a duration is the case that trips BOTH halves at once: the old
      // value's pricing rows are stranded and the new value may be one nothing
      // prices. Reported in one toast rather than two so the second is not
      // buried under the first.
      const warning = buildDurationWriteWarning(result);
      if (warning) {
        toast({
          title: translate('game_durations.duration_updated'),
          description: warning,
          status: 'warning',
          duration: 10000,
          isClosable: true,
          position: 'top-right',
        });
        setIsEditing(false);
        return;
      }
      toast({
        title: translate('game_durations.duration_updated'),
        status: 'success',
        duration: 3000,
        position: 'top-right',
      });
      setIsEditing(false);
    }
  );

  const { mutateAsync: deleteGameDuration, isLoading: isDeleting } = useDeleteOrganizationGameDuration(
    selectedOrganization?.id || '',
    (result) => {
      // Deleting a duration leaves its pricing rows unsellable — the courts
      // stay configured, nothing can book them at that length.
      const warning = buildDurationWriteWarning(result);
      if (warning) {
        toast({
          title: translate('game_durations.duration_deleted'),
          description: warning,
          status: 'warning',
          duration: 10000,
          isClosable: true,
          position: 'top-right',
        });
        onClose();
        return;
      }
      toast({
        title: translate('game_durations.duration_deleted'),
        status: 'success',
        duration: 3000,
        position: 'top-right',
      });
      onClose();
    }
  );

  const handleUpdate = async () => {
    if (!selectedOrganization?.id) {
      setIsEditing(false);
      return;
    }

    const parsedValue = parseInt(inputValue, 10);
    if (isNaN(parsedValue) || parsedValue < 1 || parsedValue > 480) {
      toast({
        title: translate('game_durations.error'),
        description: translate('game_durations.invalid_duration'),
        status: 'error',
        duration: 3000,
        position: 'top-right',
      });
      return;
    }

    if (parsedValue === gameDuration.durationMinutes) {
      setIsEditing(false);
      return;
    }

    const updateData: UpdateOrganizationSportGameDurationDto = {
      durationMinutes: parsedValue,
    };

    try {
      await updateGameDuration(updateData);
    } catch (error: any) {
      toast({
        title: translate('game_durations.error'),
        description: error?.message || translate('game_durations.error_updating'),
        status: 'error',
        duration: 5000,
        position: 'top-right',
      });
    }
  };

  const handleDelete = async () => {
    if (!selectedOrganization?.id) return;

    try {
      await deleteGameDuration(gameDuration.id);
    } catch (error: any) {
      toast({
        title: translate('game_durations.error'),
        description: error?.message || translate('game_durations.error_deleting'),
        status: 'error',
        duration: 5000,
        position: 'top-right',
      });
    }
  };

  const handleCancel = () => {
    setInputValue(String(gameDuration.durationMinutes));
    setIsEditing(false);
  };

  return (
    <>
      <Box p={4} border="1px" borderColor={cardBorderColor} borderRadius="md" bg={cardBg}>
        <HStack justify="space-between" align="center">
          <HStack spacing={4}>

            {isEditing ? (
              <NumberInput
                value={inputValue}
                onChange={(valueAsString) => {
                  setInputValue(valueAsString);
                }}
                min={1}
                max={480}
                size="sm"
                width="100px"
              >
                <NumberInputField />
              </NumberInput>
            ) : (
              <Text>
                {gameDuration.durationMinutes} {translate('game_durations.duration_minutes').toLowerCase()}
              </Text>
            )}
          </HStack>

          <HStack spacing={2}>
            {isEditing ? (
              <>
                <IconButton
                  aria-label={translate('game_durations.save')}
                  icon={<MdSave />}
                  size="sm"
                  colorScheme="green"
                  onClick={handleUpdate}
                  isLoading={isUpdating}
                />
                <IconButton
                  aria-label={translate('game_durations.cancel')}
                  icon={<MdCancel />}
                  size="sm"
                  variant="outline"
                  onClick={handleCancel}
                />
              </>
            ) : (
              <>
                <IconButton
                  aria-label={translate('game_durations.edit_duration')}
                  icon={<MdEdit />}
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setInputValue(String(gameDuration.durationMinutes));
                    setIsEditing(true);
                  }}
                />
                <IconButton
                  aria-label={translate('game_durations.delete_duration')}
                  icon={<MdDelete />}
                  size="sm"
                  colorScheme="red"
                  variant="outline"
                  onClick={onOpen}
                  isLoading={isDeleting}
                />
              </>
            )}
          </HStack>
        </HStack>
      </Box>

      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {translate('game_durations.delete_duration')}
            </AlertDialogHeader>

            <AlertDialogBody>
              {translate('game_durations.confirm_delete')}
              <Text mt={2} fontWeight="semibold">
                {gameDuration.sport} - {gameDuration.durationMinutes} {translate('game_durations.duration_minutes').toLowerCase()}
              </Text>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose}>
                {translate('game_durations.cancel')}
              </Button>
              <Button
                colorScheme="red"
                onClick={handleDelete}
                ml={3}
                isLoading={isDeleting}
              >
                {translate('game_durations.delete')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}